/**
 * useSlotCheck — the live "is that time already booked?" check behind the
 * callback card's book and edit forms (owner ask 1).
 *
 * Once a full date and time is chosen (and on every recruiter change) it reads
 * the recruiter's availability for that local day, debounced, with the stale
 * request aborted, and reports what a call there would collide with. The
 * recruiter can override with one press; the override belongs to that exact
 * (recruiter, time) pair, so changing either resets it. A failed read reports
 * nothing (fail open): the server's own check on the write is the backstop.
 */
import { useCallback, useEffect, useState } from 'react';
import { getCallbackAvailability } from './api';
import { Conflict, localDayRange, overlapsFor, parseLocalInput, tenantDayKeyOf } from './slotLogic';

export const SLOT_CHECK_DEBOUNCE_MS = 300;

export interface SlotCheck {
  /** What the chosen time collides with ([] while unknown, clear, or the read failed). */
  conflicts: Conflict[];
  /** The recruiter pressed Override for this exact recruiter and time. */
  overridden: boolean;
  /** An overlap is showing and has not been overridden: the submit waits. */
  blocking: boolean;
  checking: boolean;
  override: () => void;
  /** Carry an override made elsewhere (the slot picker) onto a (recruiter, time) pair. */
  presetOverride: (assignee: string, value: string) => void;
}

export function useSlotCheck({
  enabled,
  assignee,
  value,
  durationMin,
  excludeId,
}: {
  enabled: boolean;
  assignee: string;
  /** The form's "YYYY-MM-DDTHH:MM" value ('' until complete). */
  value: string;
  /** The call's length; absent = the tenant's slot length from the read. */
  durationMin?: number;
  /** The callback being edited, left out of its own busy list. */
  excludeId?: string | null;
}): SlotCheck {
  const pairKey = `${assignee}|${value}`;
  const live = enabled && !!assignee && !!parseLocalInput(value);
  const key = live ? `${pairKey}|${excludeId || ''}|${durationMin ?? ''}` : null;

  const [result, setResult] = useState<{ key: string; conflicts: Conflict[] } | null>(null);
  const [checking, setChecking] = useState(false);
  const [overrideKey, setOverrideKey] = useState<string | null>(null);

  useEffect(() => {
    if (!key) {
      setChecking(false);
      return undefined;
    }
    const range = localDayRange(value);
    const start = parseLocalInput(value);
    if (!range || !start) return undefined;
    const ctrl = new AbortController();
    let alive = true;
    setChecking(true);
    const timer = window.setTimeout(() => {
      getCallbackAvailability({ assignee, from: range.from, to: range.to, exclude: excludeId || null }, ctrl.signal)
        .then((av) => {
          if (!alive) return;
          const dur = durationMin ?? av.booking.slot_minutes;
          setResult({ key, conflicts: overlapsFor(av.busy, av.absent_days, start.getTime(), dur, tenantDayKeyOf(av.timezone)) });
        })
        .catch(() => {
          // Fail open: no alert. The server refuses a real overlap on the write.
          if (alive) setResult({ key, conflicts: [] });
        })
        .finally(() => {
          if (alive) setChecking(false);
        });
    }, SLOT_CHECK_DEBOUNCE_MS);
    return () => {
      alive = false;
      window.clearTimeout(timer);
      ctrl.abort();
    };
    // `key` already folds in assignee, value, excludeId and durationMin.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const conflicts = key && result?.key === key ? result.conflicts : [];
  // Keyed to the pair, not to the read: an override carried in from the picker
  // holds while this form's own read is still in flight.
  const overridden = live && overrideKey === pairKey;

  const override = useCallback(() => setOverrideKey(pairKey), [pairKey]);
  const presetOverride = useCallback((a: string, v: string) => setOverrideKey(`${a}|${v}`), []);

  return {
    conflicts,
    overridden,
    blocking: conflicts.length > 0 && !overridden,
    checking,
    override,
    presetOverride,
  };
}
